/**
 * Sector Topics Availability Panel
 *
 * Check sector topics availability and generate topics from product feed
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, CheckCircle2, Clock, AlertCircle, Sparkles, Database, ArrowRight } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface TopicsMetrics {
  totalIdentified: number;
  availableResearched: number;
  pendingApproval: number;
  pendingResearch: number;
}

interface ProductFeed {
  id: string;
  file_name: string;
  records_count: number;
  embeddings_count: number;
  effective_records_count: number; // KB entries (products that can be processed)
}

interface SectorTopicsAvailabilityPanelProps {
  siteId: string;
  sectorId?: number | null;
}

export function SectorTopicsAvailabilityPanel({ siteId, sectorId }: SectorTopicsAvailabilityPanelProps) {
  const [metrics, setMetrics] = useState<TopicsMetrics | null>(null);
  const [productFeed, setProductFeed] = useState<ProductFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  // Debug logging
  useEffect(() => {
    console.log('[SectorTopicsAvailabilityPanel] Props:', { siteId, sectorId, sectorIdType: typeof sectorId });
  }, [siteId, sectorId]);

  useEffect(() => {
    loadData();
  }, [siteId, sectorId]);

  const loadData = async () => {
    if (!siteId || !sectorId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Load product feed info
      const feedResponse = await authFetch(`/data-sources/site/${siteId}/product-feed`);
      const feedData = await feedResponse.json();

      if (feedData.success && feedData.dataSource) {
        setProductFeed(feedData.dataSource);

        // Load topics metrics for this sector
        const metricsResponse = await authFetch(`/qa/sectors/${sectorId}/topics/metrics`);
        const metricsData = await metricsResponse.json();

        if (metricsData.success) {
          setMetrics(metricsData.metrics);
        }
      }
    } catch (error) {
      console.error('Failed to load sector topics data:', error);
      toast({
        title: "Error",
        description: "Failed to load sector topics information",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTopics = async () => {
    if (!productFeed || !sectorId) return;

    setGenerating(true);
    try {
      // Use the full record count from the product feed
      const maxProducts = productFeed.records_count || null;

      const response = await authFetch(`/data-sources/${productFeed.id}/generate-topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sectorId,
          siteId,
          maxProducts, // Use full catalog
          useAttributeAnalysis: true
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Topics Generated",
          description: `Identified ${data.newTopics || 0} new topics and matched ${data.matchedTopics || 0} existing topics`,
        });

        // Reload metrics
        await loadData();
      } else {
        throw new Error(data.message || 'Failed to generate topics');
      }
    } catch (error: any) {
      console.error('Failed to generate topics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate topics from product feed",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!sectorId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This site needs to be assigned to a sector before topic generation can begin.
              Please contact your administrator to assign a sector to this site.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!productFeed) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No product feed found for this site. Please upload a product feed first
              in the Data Sources section before generating Q&A topics.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Sector Intelligence Overview
          </CardTitle>
          <CardDescription>
            PageMind maintains sector-level research on product attributes, technologies, and use cases.
            This intelligence powers the Q&A system and is continuously enhanced as sites contribute data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <Database className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              Your site builds upon Pagemind's sector intelligence. When you analyze your product feed,
              we identify research topics specific to your products and map them to our existing sector knowledge.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Product Feed Status */}
      <Card>
        <CardHeader>
          <CardTitle>Product Feed Status</CardTitle>
          <CardDescription>Your uploaded product feed that will be analyzed for topics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{productFeed.file_name}</p>
              <p className="text-sm text-muted-foreground">
                {productFeed.records_count.toLocaleString()} products • {productFeed.embeddings_count.toLocaleString()} embeddings
              </p>
            </div>
            {productFeed.embeddings_count >= productFeed.effective_records_count * 0.99 ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Ready
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                <Clock className="h-3 w-3 mr-1" />
                Processing
              </Badge>
            )}
          </div>

          <Button
            onClick={handleGenerateTopics}
            disabled={generating || productFeed.embeddings_count < productFeed.effective_records_count * 0.99}
            className="w-full"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing Product Feed...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze Product Feed for Topics
              </>
            )}
          </Button>

          {productFeed.embeddings_count < productFeed.effective_records_count * 0.99 && (
            <p className="text-sm text-muted-foreground text-center">
              Waiting for embeddings to complete ({((productFeed.embeddings_count / productFeed.effective_records_count) * 100).toFixed(1)}%)
            </p>
          )}
        </CardContent>
      </Card>

      {/* Topics Metrics */}
      {metrics && (
        <Card>
          <CardHeader>
            <CardTitle>Sector Topics Status</CardTitle>
            <CardDescription>
              Topics availability for your sector in Pagemind's intelligence database
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-2">
                <div className="text-2xl font-bold">{metrics.totalIdentified}</div>
                <div className="text-sm text-muted-foreground">Total Topics Identified</div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="text-2xl font-bold text-green-600">{metrics.availableResearched}</div>
                <div className="text-sm text-muted-foreground">Available & Researched</div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="text-2xl font-bold text-yellow-600">{metrics.pendingApproval}</div>
                <div className="text-sm text-muted-foreground">Pending Approval</div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="text-2xl font-bold text-blue-600">{metrics.pendingResearch}</div>
                <div className="text-sm text-muted-foreground">Pending Research</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              1
            </div>
            <div>
              <h4 className="font-semibold mb-1">Analyze Your Product Feed</h4>
              <p className="text-sm text-muted-foreground">
                Click the button above to identify research topics from your products' attributes, technologies, and features.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              2
            </div>
            <div>
              <h4 className="font-semibold mb-1">Topics Get Reviewed</h4>
              <p className="text-sm text-muted-foreground">
                New topics are sent to the Pagemind team for review and approval. We verify relevance and quality.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              3
            </div>
            <div>
              <h4 className="font-semibold mb-1">Research Gets Generated</h4>
              <p className="text-sm text-muted-foreground">
                Approved topics are researched by our AI system, creating comprehensive knowledge documents about each topic.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              4
            </div>
            <div>
              <h4 className="font-semibold mb-1">Question Wheels Created</h4>
              <p className="text-sm text-muted-foreground">
                The Pagemind team generates question wheels from the research, which become available for your site to use for product mapping and answer generation.
              </p>
            </div>
          </div>

          {metrics && metrics.availableResearched > 0 && (
            <Alert className="bg-green-50 border-green-200 mt-6">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <strong>Good news!</strong> Your sector already has {metrics.availableResearched} researched topics available.
                These can be used immediately for generating Q&A content.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
